import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clapperboard, Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060D18] flex items-center justify-center px-5 grain">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md rounded-2xl border border-[#C5A059]/20 bg-[#0C172A] p-8 shadow-2xl"
      >
        <div className="flex flex-col items-center text-center mb-8">
          <div className="h-12 w-12 rounded-xl bg-[#12213D] border border-[#C5A059]/30 flex items-center justify-center">
            <Lock className="h-6 w-6 text-[#C5A059]" />
          </div>
          <h1 className="font-display text-2xl text-white mt-4">CRM Dashboard</h1>
          <p className="text-sm text-[#94A3B8] mt-1 flex items-center gap-1.5">
            <Clapperboard className="h-4 w-4" /> Lights, Camera, Take Action
          </p>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Email</Label>
            <Input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#060D18] border-[#C5A059]/20 text-white h-12 focus-visible:ring-[#C5A059]"
              placeholder="you@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Password</Label>
            <Input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#060D18] border-[#C5A059]/20 text-white h-12 focus-visible:ring-[#C5A059]"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p className="text-sm text-[#E50914]" data-testid="login-error">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full bg-[#C5A059] hover:bg-[#D4AF37] text-[#060D18] font-semibold rounded-full h-12 transition-all"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
          </Button>
        </form>
        <a
          href="/"
          className="block text-center text-xs text-[#64748B] hover:text-[#C5A059] mt-6"
        >
          ← Back to site
        </a>
      </motion.div>
    </div>
  );
}
